/**
 * Le calendrier d'une feuille de temps, sans une ligne de rendu.
 *
 * Tout ici manipule des jours **calendaires** (« 2026-09-03 ») et jamais des
 * instants : une feuille compte des jours, pas des heures, et le fuseau du
 * navigateur ne doit pas décaler un jour. D'où deux règles absolues :
 *
 * - jamais `new Date("YYYY-MM-DD")` : ce constructeur lit la chaîne en UTC et
 *   rend, à Paris, la veille à 2 h du matin ;
 * - une date se construit par ses composantes (`new Date(a, m - 1, j)`) et se
 *   relit par ses accesseurs locaux (`getDate()`, `getDay()`).
 *
 * Les jours et mois du contrat sont des chaînes ISO ; on les fabrique et on
 * les compare comme des chaînes, ce qui est exact parce que le format est à
 * largeur fixe.
 */
import type { TimesheetEntry } from "../../api";

/** Un mois calendaire. `mois` va de 1 à 12. */
export type Mois = { annee: number; mois: number };

/** 0 : rien ; 1 : une demi-journée ; 2 : une journée. */
export type DemiJournees = 0 | 1 | 2;

/** La grille d'un mois : les jours déclarés, par jour ISO. Absent = 0. */
export type Grille = Record<string, DemiJournees>;

const NOMS_MOIS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const NOMS_JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];

/** Les sept colonnes de la grille, lundi en tête, comme un agenda français. */
export const ENTETES_SEMAINE = ["L", "M", "M", "J", "V", "S", "D"] as const;
export const NOMS_SEMAINE = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"] as const;

function deuxChiffres(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** « 2026-09 » → `{ annee: 2026, mois: 9 }`, ou `null`. */
export function parseMois(s: string): Mois | null {
  const lu = /^(\d{4})-(\d{2})$/.exec(s);
  if (lu === null) {
    return null;
  }
  const annee = Number.parseInt(lu[1], 10);
  const mois = Number.parseInt(lu[2], 10);
  if (mois < 1 || mois > 12) {
    return null;
  }
  return { annee, mois };
}

/** `{ 2026, 9 }` → « 2026-09 », la forme du contrat. */
export function formatMois(m: Mois): string {
  return `${m.annee}-${deuxChiffres(m.mois)}`;
}

/** `{ 2026, 9 }` → « septembre 2026 ». */
export function libelleMois(m: Mois): string {
  return `${NOMS_MOIS[m.mois - 1]} ${m.annee}`;
}

/** Le mois courant, dans le fuseau du navigateur. */
export function moisCourant(maintenant: Date = new Date()): Mois {
  return { annee: maintenant.getFullYear(), mois: maintenant.getMonth() + 1 };
}

export function moisPrecedent(m: Mois): Mois {
  return m.mois === 1 ? { annee: m.annee - 1, mois: 12 } : { annee: m.annee, mois: m.mois - 1 };
}

/** Les `n` derniers mois, du courant au plus ancien. */
export function derniersMois(n: number, depuis: Mois = moisCourant()): Mois[] {
  const out: Mois[] = [];
  let m = depuis;
  for (let i = 0; i < n; i++) {
    out.push(m);
    m = moisPrecedent(m);
  }
  return out;
}

/** Nombre de jours du mois : le jour 0 du mois suivant est le dernier de celui-ci. */
export function nombreDeJours(m: Mois): number {
  return new Date(m.annee, m.mois, 0).getDate();
}

/** Tous les jours du mois, en ISO, du 1er au dernier. */
export function joursDuMois(m: Mois): string[] {
  const n = nombreDeJours(m);
  const out: string[] = [];
  for (let j = 1; j <= n; j++) {
    out.push(`${formatMois(m)}-${deuxChiffres(j)}`);
  }
  return out;
}

/** Composantes d'un jour ISO, sans passer par `Date`. */
function composantes(iso: string): { annee: number; mois: number; jour: number } {
  return {
    annee: Number.parseInt(iso.slice(0, 4), 10),
    mois: Number.parseInt(iso.slice(5, 7), 10),
    jour: Number.parseInt(iso.slice(8, 10), 10),
  };
}

/** Jour de la semaine d'un jour ISO, lundi = 0 … dimanche = 6. */
export function jourDeSemaine(iso: string): number {
  const { annee, mois, jour } = composantes(iso);
  return (new Date(annee, mois - 1, jour).getDay() + 6) % 7;
}

/**
 * Colonnes vides avant le 1er du mois pour que chaque jour tombe sous son
 * nom : un mois qui commence un dimanche en laisse six.
 */
export function decalageLundi(m: Mois): number {
  return jourDeSemaine(`${formatMois(m)}-01`);
}

/** Samedi ou dimanche. Saisissables, mais distingués à l'écran. */
export function estWeekend(iso: string): boolean {
  return jourDeSemaine(iso) >= 5;
}

/** Aujourd'hui, en ISO, dans le fuseau du navigateur. */
export function aujourdhuiLocal(maintenant: Date = new Date()): string {
  return `${maintenant.getFullYear()}-${deuxChiffres(maintenant.getMonth() + 1)}-${deuxChiffres(maintenant.getDate())}`;
}

/** Vrai si `iso` est après `aujourdhui`. Comparaison de chaînes ISO : exacte. */
export function estFutur(iso: string, aujourdhui: string): boolean {
  return iso > aujourdhui;
}

/** « 2026-09-03 » → « mercredi 3 septembre ». */
export function libelleJour(iso: string): string {
  const { annee, mois, jour } = composantes(iso);
  const nomJour = NOMS_JOURS[new Date(annee, mois - 1, jour).getDay()];
  return `${nomJour} ${jour} ${NOMS_MOIS[mois - 1]}`;
}

/** Le clic suivant : rien → demi-journée → journée → rien. */
export function cycle(v: DemiJournees): DemiJournees {
  return v === 0 ? 1 : v === 1 ? 2 : 0;
}

/** Ce que dit une valeur, pour l'étiquette du bouton. */
export function libelleDemiJournees(v: DemiJournees): string {
  return v === 0 ? "rien" : v === 1 ? "demi-journée" : "journée";
}

/** La grille vers le corps de la requête : les zéros disparaissent. */
export function versEntries(grille: Grille): TimesheetEntry[] {
  return Object.keys(grille)
    .filter((jour) => grille[jour] !== 0)
    .sort()
    .map((jour) => ({ day: jour, half_days: grille[jour] as 1 | 2 }));
}

/** Les jours reçus vers la grille. */
export function depuisEntries(entries: readonly TimesheetEntry[]): Grille {
  const grille: Grille = {};
  for (const e of entries) {
    grille[e.day] = e.half_days;
  }
  return grille;
}

/** Somme des demi-journées de la grille. */
export function totalDemiJournees(grille: Grille): number {
  let total = 0;
  for (const v of Object.values(grille)) {
    total += v;
  }
  return total;
}

/** `5` demi-journées → « 2,5 jours » ; `1` → « 0,5 jour » ; `2` → « 1 jour ». */
export function formatJours(demiJournees: number): string {
  const entier = Math.floor(demiJournees / 2);
  const demi = demiJournees % 2 === 1;
  const texte = demi ? `${entier},5` : String(entier);
  return `${texte} ${demiJournees > 2 ? "jours" : "jour"}`;
}

/** Vrai si les deux grilles déclarent les mêmes jours. */
export function memeGrille(a: Grille, b: Grille): boolean {
  const ea = versEntries(a);
  const eb = versEntries(b);
  if (ea.length !== eb.length) {
    return false;
  }
  return ea.every((e, i) => e.day === eb[i].day && e.half_days === eb[i].half_days);
}
